import { dramas, type Drama } from "@/mocks/dramas";
import { actors, type Actor } from "@/mocks/actors";
import { specials } from "@/mocks/specials";

export function getDramaById(id: string): Drama | undefined {
  return dramas.find((item) => item.id === id);
}

export function getActorById(id: string): Actor | undefined {
  return actors.find((item) => item.id === id);
}

export function getActorName(id: string): string {
  const actor = getActorById(id);
  return actor ? actor.name : id;
}

export function getSpecials() {
  return specials;
}

export function getSpecialById(id: string) {
  return specials.find((item) => item.id === id);
}

export function getDramasByIds(ids: string[]): Drama[] {
  return ids.map((id) => getDramaById(id)).filter((item): item is Drama => Boolean(item));
}

export function formatPopularity(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  return `${value}`;
}

export function formatDate(value: string): string {
  return value.replace(/-/g, "-");
}

export function getFeaturedDramas(): Drama[] {
  return dramas.filter((item) => item.featured);
}

export function getLatestDramas(limit = 8): Drama[] {
  return [...dramas].sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1)).slice(0, limit);
}

export function getHotDramas(limit = 6): Drama[] {
  return [...dramas].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}

export function getRelatedDramas(drama: Drama, limit = 4): Drama[] {
  const scored = dramas
    .filter((item) => item.id !== drama.id)
    .map((item) => {
      let score = 0;
      if (item.category === drama.category) score += 3;
      if (item.genre === drama.genre) score += 3;
      const sharedTags = item.tags.filter((tag) => drama.tags.includes(tag));
      score += sharedTags.length * 2;
      if (item.year === drama.year) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function searchDramas(keyword: string): Drama[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return dramas.filter((item) => {
    const actorNames = item.cast.map((id) => getActorName(id)).join(" ");
    return (
      item.title.toLowerCase().includes(key) ||
      item.genre.toLowerCase().includes(key) ||
      item.category.toLowerCase().includes(key) ||
      item.synopsis.toLowerCase().includes(key) ||
      item.tags.some((tag) => tag.toLowerCase().includes(key)) ||
      item.director.toLowerCase().includes(key) ||
      actorNames.toLowerCase().includes(key)
    );
  });
}

export interface FilterState {
  category: string;
  genre: string;
  year: string;
  region: string;
  sort: string;
}

export function filterDramas(state: FilterState): Drama[] {
  const result = dramas.filter((item) => {
    if (state.category !== "全部" && item.category !== state.category) return false;
    if (state.genre !== "全部" && item.genre !== state.genre) return false;
    if (state.year !== "全部" && String(item.year) !== state.year) return false;
    if (state.region !== "全部" && item.region !== state.region) return false;
    return true;
  });

  if (state.sort === "popular") {
    return result.sort((a, b) => b.popularity - a.popularity);
  }
  if (state.sort === "rating") {
    return result.sort((a, b) => b.rating - a.rating);
  }
  return result.sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1));
}